import { getApiBase } from "./apiBase";
import { dropSession, isAuthError } from "./session";

/**
 * Награды участника: Юбилейная лицензия, Flower Box, карточки стихий.
 *
 * Ответ /v1/birthday/my-awards:
 *   awards[]    — key, id, kind, title, description, count, status, paid_at,
 *                 order_id; у Flower Box ещё activates_with
 *   total       — сколько наград всего
 *   entitlement — что они дают: location_months, can_redeem, to_next, hint
 *   pending_payments[] — заявки, по которым оплата не подтвердилась:
 *                 id, status (pending | review), order_id, payment_id, count,
 *                 amount, created_at, waiting_minutes, needs_support
 *
 * Про два похожих поля, на которых легко ошибиться:
 *   key — уникален для строки списка ('license-17', 'flowerbox-17');
 *   id  — это id ЗАЯВКИ, и он одинаковый у всех наград одной покупки.
 * Значит собрать награды одного платежа можно фильтром по id, а вот ключом
 * для React или для поиска конкретной награды он не годится — возьмите key.
 *
 * status: active | pending. Награды отдаются только по оплаченным заявкам
 * (RequestLk::STATUS_PAID), свежая покупка идёт первой — сортировка по
 * paid_at по убыванию.
 *
 * Пользователь определяется по токену из localStorage.
 *
 * Ошибку никогда не превращаем в пустой список. Раньше 404 отдавался как
 * «наград нет»: считалось, что ручки может не быть на контуре. Но снаружи
 * «ручка не выложена» и «покупок не было» выглядят одинаково, а значат
 * противоположное — и окно итога покупки говорило человеку, у которого
 * списаны деньги, «ждите, всё хорошо», хотя ждать было нечего. Пусть лучше
 * раздел покажет ошибку: неудобная правда чинится, ложное спокойствие — нет.
 */
export const fetchAwards = async () => {
  const base = getApiBase();
  if (!base) {
    throw new Error("не задан адрес API");
  }

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${base}/v1/birthday/my-awards`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });

  const data = await res.json().catch(() => null);

  if (isAuthError(res.status, data && data.message)) {
    dropSession();
    const error = new Error("сессия истекла, войдите заново");
    error.status = res.status;
    throw error;
  }

  if (!res.ok) {
    const text = (data && (data.message || data.error)) || `код ${res.status}`;
    const error = new Error(text);
    error.status = res.status;
    throw error;
  }

  /* Ответ с кодом 200, но без awards — это не «наград нет», а чужой ответ:
     так выглядит заглушка прокси или страница ошибки, отданная с кодом 200.
     Отличить их можно только по форме тела, и делать это надо здесь: выше
     пустой массив уже неотличим от честного «покупок не было». */
  if (!data || !Array.isArray(data.awards)) {
    const error = new Error("сервер ответил не тем, что ожидает кабинет");
    error.status = res.status;
    throw error;
  }

  return data;
};

/** Активация награды: помечает выданной и начисляет, если начисление не прошло. */
export const activateAward = async (id) => {
  const base = getApiBase();
  const token = localStorage.getItem("token");

  const res = await fetch(`${base}/v1/birthday/activate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ id }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data && data.error ? data.error : `код ${res.status}`);
  }

  return data;
};
